import React, { useState, useEffect, useCallback } from 'react';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { FaTrash, FaCamera } from 'react-icons/fa';
import debounce from 'lodash/debounce';

const ToolForm = ({ onSubmit, categories, initialData = null, onImageDelete }) => {
  const [localToolData, setLocalToolData] = useState({
    name: '',
    description: '',
    category: '',
    imageFiles: [],
    imageUrls: [],
    availability: true
  });
  const [existingImages, setExistingImages] = useState([]);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    console.log('ToolForm: initialData changed', initialData);
    if (initialData) {
      setLocalToolData({
        ...initialData,
        imageFiles: []
      });
      setExistingImages(initialData.imageUrls || []);
    }
  }, [initialData]);

  const debouncedSetLocalToolData = useCallback(
    debounce((newData) => {
      console.log('ToolForm: debouncedSetLocalToolData called', newData);
      setLocalToolData(newData);
    }, 300),
    []
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    console.log(`ToolForm: handleChange called for ${name}`, newValue);
    debouncedSetLocalToolData((prevData) => {
      const updatedData = { ...prevData, [name]: newValue };
      console.log('ToolForm: Updated localToolData', updatedData);
      return updatedData;
    });
  };

  // ... (rest of the component remains the same)

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('ToolForm: handleSubmit called');
    setError(null);
    setIsProcessing(true);

    const totalImages = existingImages.length + localToolData.imageFiles.length;
    if (totalImages < 1 || totalImages > 5) {
      setError('Please have 1-5 images in total');
      setIsProcessing(false);
      return;
    }

    try {
      const newImageUrls = await Promise.all(
        localToolData.imageFiles.map(async (file) => {
          const storageRef = ref(storage, `tool-images/${file.name}`);
          await uploadBytes(storageRef, file);
          return getDownloadURL(storageRef);
        })
      );

      const allImageUrls = [...existingImages, ...newImageUrls];
      
      const { imageFiles, ...toolDataWithoutFiles } = localToolData;
      
      console.log('ToolForm: Submitting tool data', toolDataWithoutFiles);
      await onSubmit({ ...toolDataWithoutFiles, imageUrls: allImageUrls });
    } catch (error) {
      console.error('Error submitting tool data:', error);
      setError(`Error submitting tool data: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  console.log('ToolForm: Rendering with localToolData', localToolData);

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      {/* ... (form fields remain the same, but use localToolData instead of toolData) */}
      <div className="mb-4">
        <label htmlFor="name" className="block mb-2 font-bold text-gray-700">Tool Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={localToolData.name}
          onChange={handleChange}
          className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:border-blue-500"
          required
          aria-label="Tool Name"
        />
      </div>
      {/* ... (other form fields) */}
      <button 
        type="submit" 
        className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        disabled={isProcessing}
      >
        {isProcessing ? 'Processing...' : (initialData ? 'Update Tool' : 'Add Tool')}
      </button>
    </form>
  );
};

export default ToolForm;